import EditIcon from '@mui/icons-material/Edit'
import { Box, Typography } from '@mui/material'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import Grid from '@mui/material/Grid'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import { getFechaFromISO } from 'common/libs/fechas'
import { CustomThemeContext } from 'common/providers/CustomThemeProvider'
import CamposAuxiliares from 'features/datos_trabajador/components/CamposAuxiliares'
import { selectPortal } from 'features/portal/portalSlice'
import BotonSolicitudInterna from 'features/solicitudes/components/BotonSolicitudInterna'
import i18n from 'lang/idiomas'
import { useContext, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

/**
 * Visor de datos personales del trabajador
 *
 * @param {Object} props
 * @param {WorkerDataDto} [props.datosTrabajador = null] - Worker data
 * @param {Object} [props.otrosDatos = null] - Other worker data including auxiliary fields
 */
const Personales = ({ datosTrabajador = null, otrosDatos = null }) => {
  const { themeController } = useContext(CustomThemeContext)

  const fecha_nacimiento = new Date(
    datosTrabajador?.fecha_nacimiento?.replace(/(\d{2})-(\d{2})-(\d{4})/, '$2/$1/$3'),
  )
  const edad = useMemo(() => {
    const now = new Date()
    return new Date(now - fecha_nacimiento).getUTCFullYear() - 1970
  }, [fecha_nacimiento])
  const location = useLocation()

  const [user, _] = useState(location?.state?.user ?? null)
  const portal = useSelector(selectPortal)

  const locale = portal?.locale
  return (
    <>
      <Box sx={{ my: 3, display: 'flex', gap: 2 }}>
        <Typography variant="h3">{i18n.t('persoles', { defaultValue: 'Personales' })}</Typography>
        {!user ? (
          <BotonSolicitudInterna
            id_solicitud_pe="DPRUP"
            titulo={<EditIcon />}
            buttonVariant="contained"
            buttonSize="small"
            type="button"
            fabIcon={<EditIcon />}
            sx={{
              height: '48px',
              width: '48px',
              minWidth: '48px',
              '& a': { display: 'flex !important', justifyContent: 'center' },
            }}
            tooltip={i18n.t('solcamb', { defaultValue: 'Solicitar cambio de datos' })}
          />
        ) : null}
      </Box>
      <Stack pt={2} pb={2} spacing={2}>
        <Grid className="outline-paper" container spacing={2}>
          <Grid item size={{ xs: 12, md: 4 }}>
            <Card sx={{ flexGrow: 1 }}>
              <CardHeader title={i18n.t('datpers', { defaultValue: 'Datos Personales' })} />
              <CardContent>
                <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                  <ListItem>
                    <ListItemText
                      primary={i18n.t('nmbr', { defaultValue: 'Nombre' })}
                      secondary={`${datosTrabajador?.nombre ?? ''}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={i18n.t('primape', { defaultValue: 'Primer apellido' })}
                      secondary={`${datosTrabajador?.primer_apellido ?? ''}`}
                    />
                  </ListItem>
                  {themeController.hide?.WorkerData?.Personal?.SegundoApellido ? (
                    <></>
                  ) : (
                    <ListItem>
                      <ListItemText
                        primary={i18n.t('segape', { defaultValue: 'Segundo apellido' })}
                        secondary={`${datosTrabajador?.segundo_apellido ?? ''}`}
                      />
                    </ListItem>
                  )}
                  {themeController.hide?.WorkerData?.Personal?.NombreAbreviado ? (
                    <></>
                  ) : (
                    <ListItem>
                      <ListItemText
                        primary={i18n.t('nombabr', { defaultValue: 'Nombre abreviado' })}
                        secondary={`${datosTrabajador?.nombre_abreviado ?? ''}`}
                      />
                    </ListItem>
                  )}
                  <ListItem>
                    <ListItemText
                      primary={i18n.t('sexo', { defaultValue: 'Sexo' })}
                      secondary={datosTrabajador?.d_sexo ?? ''}
                    />
                  </ListItem>
                  <CamposAuxiliares otrosDatos={otrosDatos} subseccion="DPER" locale={locale} />
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item size={{ xs: 12, md: 4 }}>
            <Card sx={{ flexGrow: 1 }}>
              <CardHeader title={i18n.t('nacimto', { defaultValue: 'Nacimiento' })} subheader="" />
              <CardContent>
                <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                  <ListItem>
                    <ListItemText
                      primary={i18n.t('fecnac', { defaultValue: 'Fecha nacimiento' })}
                      secondary={`${
                        fecha_nacimiento
                          ? getFechaFromISO(
                              datosTrabajador?.fecha_nacimiento,
                              portal?.locale?.replace('_', '-'),
                            )
                          : ''
                      }`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={i18n.t('edad', { defaultValue: 'Edad' })}
                      secondary={`${edad ?? ''}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={i18n.t('lugar', { defaultValue: 'Lugar' })}
                      secondary={`${datosTrabajador?.lugar_nacimiento ?? ''}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={i18n.t('provnac', { defaultValue: 'Provincia' })}
                      secondary={`${datosTrabajador?.d_provincia_nacim ?? ''}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={i18n.t('estnac', { defaultValue: 'Estado' })}
                      secondary={`${datosTrabajador?.d_estado_nacim ?? ''}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={i18n.t('nacion', { defaultValue: 'Nacionalidad' })}
                      secondary={`${datosTrabajador?.d_nacionalidad ?? ''}`}
                    />
                  </ListItem>
                  <CamposAuxiliares otrosDatos={otrosDatos} subseccion="NACI" locale={locale} />
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item size={{ xs: 12, md: 4 }}>
            <Card sx={{ flexGrow: 1, mb: 2 }}>
              <CardHeader title={i18n.t('movilid', { defaultValue: 'Movilidad' })} subheader="" />
              <CardContent>
                <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                  <ListItem>
                    <ListItemText
                      primary={i18n.t('carnet', { defaultValue: 'Carnet de conducir' })}
                      secondary={`${datosTrabajador?.carnet_conducir ?? ''}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={i18n.t('vehiculo', { defaultValue: 'Vehículo' })}
                      secondary={`${datosTrabajador?.vehiculo ?? ''}`}
                    />
                  </ListItem>
                  <CamposAuxiliares otrosDatos={otrosDatos} subseccion="MOVI" locale={locale} />
                </List>
              </CardContent>
            </Card>

            {themeController.hide?.WorkerData?.Personal?.Emergency?.Hide ? (
              <></>
            ) : (
              <Card
                className="emergencia"
                sx={{
                  flexGrow: 1,
                  alignSelf: 'flex-start',
                  bgcolor: 'error.dark',
                  color: 'error.contrastText',
                }}
              >
                <CardHeader title={i18n.t('emergen', { defaultValue: 'Emergencia' })} />
                <CardContent>
                  <List sx={{ width: '100%', bgcolor: 'error.dark', color: 'error.contrastText' }}>
                    <ListItem>
                      <ListItemText
                        color="inherit"
                        primary={i18n.t('contemrg', { defaultValue: 'Contacto de emergencia' })}
                        secondary={
                          <Typography>{`${
                            otrosDatos?.contacto_emergencia_nombre ?? ''
                          }`}</Typography>
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        color="inherit"
                        primary={i18n.t('telemrg', { defaultValue: 'Teléfono de emergencia' })}
                        secondary={
                          <Typography>{`${
                            otrosDatos?.contacto_emergencia_telefono ?? ''
                          }`}</Typography>
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={i18n.t('infadic', { defaultValue: 'Información adicional' })}
                        secondary={
                          <Typography>{`${otrosDatos?.texto_productor ?? ''}`}</Typography>
                        }
                      />
                    </ListItem>
                    <CamposAuxiliares
                      otrosDatos={otrosDatos}
                      subseccion="EMER"
                      locale={locale}
                      inheritColor
                    />
                  </List>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
        <Grid className="outline-paper" container spacing={2}>
          <Grid item size={{ xs: 12, md: 8 }}>
            <Card sx={{ flexGrow: 1 }}>
              <CardHeader title={i18n.t('residenc', { defaultValue: 'Residencia' })} subheader="" />
              <CardContent>
                <Stack spacing={0}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 0, sm: 2 },
                      width: '100%',
                    }}
                  >
                    <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                      {themeController.hide?.WorkerData?.Personal?.TipoDeVia ? (
                        <></>
                      ) : (
                        <ListItem>
                          <ListItemText
                            primary={i18n.t('tipovia', { defaultValue: 'Tipo de vía' })}
                            secondary={`${datosTrabajador?.d_calle_plaza_avda ?? ''}`}
                          />
                        </ListItem>
                      )}
                      <ListItem>
                        <ListItemText
                          primary={i18n.t('direcci', { defaultValue: 'Dirección' })}
                          secondary={`${datosTrabajador?.direccion ?? ''}`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={i18n.t('numero', { defaultValue: 'Número' })}
                          secondary={`${datosTrabajador?.numero ?? ''}`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={i18n.t('piso', { defaultValue: 'Piso' })}
                          secondary={`${datosTrabajador?.piso ?? ''}`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={i18n.t('puerta', { defaultValue: 'Puerta' })}
                          secondary={`${datosTrabajador?.puerta ?? ''}`}
                        />
                      </ListItem>
                    </List>
                    <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                      <ListItem>
                        <ListItemText
                          primary={i18n.t('general.estado', { defaultValue: 'Estado' })}
                          secondary={`${datosTrabajador?.d_estado ?? ''}`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={i18n.t('provi', { defaultValue: 'Provincia' })}
                          secondary={`${datosTrabajador?.d_provincia ?? ''}`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={i18n.t('municip', { defaultValue: 'Municipio' })}
                          secondary={`${datosTrabajador?.municipio ?? ''}`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={i18n.t('localid', { defaultValue: 'Localidad' })}
                          secondary={`${datosTrabajador?.localidad ?? ''}`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={i18n.t('cdpst', { defaultValue: 'Código Postal' })}
                          secondary={`${datosTrabajador?.codigo_postal ?? ''}`}
                        />
                      </ListItem>
                      <CamposAuxiliares otrosDatos={otrosDatos} subseccion="RESI" locale={locale} />
                    </List>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item size={{ xs: 12, md: 4 }}>
            <Card sx={{ flexGrow: 1 }}>
              <CardHeader title={i18n.t('contacto', { defaultValue: 'Contacto' })} subheader="" />
              <CardContent>
                <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                  {themeController.hide?.WorkerData?.Personal?.Telefono1 ? (
                    <></>
                  ) : (
                    <ListItem>
                      <ListItemText
                        primary={i18n.t('telef1', { defaultValue: 'Teléfono 1º' })}
                        secondary={`${datosTrabajador?.telefono1 ?? ''}`}
                      />
                    </ListItem>
                  )}
                  {themeController.hide?.WorkerData?.Personal?.Telefono2 ? (
                    <></>
                  ) : (
                    <ListItem>
                      <ListItemText
                        primary={i18n.t('telef2', { defaultValue: 'Teléfono 2º' })}
                        secondary={`${datosTrabajador?.telefono2 ?? ''}`}
                      />
                    </ListItem>
                  )}
                  <ListItem>
                    <ListItemText
                      primary={i18n.t('movpers', { defaultValue: 'Móvil personal' })}
                      secondary={`${datosTrabajador?.movil_personal ?? ''}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={i18n.t('movprof', { defaultValue: 'Móvil profesional' })}
                      secondary={`${datosTrabajador?.movil_empresa ?? ''}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={i18n.t('emaper', { defaultValue: 'Email personal' })}
                      secondary={`${datosTrabajador?.e_mail_personal ?? ''}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={i18n.t('email', { defaultValue: 'Email' })}
                      secondary={`${datosTrabajador?.e_mail ?? ''}`}
                    />
                  </ListItem>
                  <CamposAuxiliares otrosDatos={otrosDatos} subseccion="CONT" locale={locale} />
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Stack>
    </>
  )
}

export default Personales
